import { FormEvent, useEffect, useState } from 'react';
import {
  api,
  GrantableGrade,
  ManageableRole,
  SchoolUser,
  SchoolUsersResponse,
} from '../api';
import { useAuth } from '../auth-context';
import { Layout, Loading, ErrorBox } from '../components/Layout';
import {
  ScopePicker,
  ScopeSelection,
  emptySelection,
  selectionFromRows,
  selectionToEntries,
} from '../components/ScopePicker';

const field = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm';
const label = 'block text-sm font-medium text-slate-700 mb-1';
const rowBtn = 'text-xs px-2 py-1 rounded border';

const ROLE_LABEL: Record<string, string> = {
  SCHOOL_ADMIN: 'School admin',
  COORDINATOR: 'Coordinator',
  TEACHER: 'Teacher',
};

export function SchoolUsers() {
  const { me } = useAuth();
  const [data, setData] = useState<SchoolUsersResponse | null>(null);
  const [tree, setTree] = useState<GrantableGrade[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Id of the row with an action in flight, so only that row disables. */
  const [busy, setBusy] = useState<string | null>(null);

  /** New-user form scope. */
  const [newScope, setNewScope] = useState<ScopeSelection>(emptySelection);

  /** The user whose scope is being edited, if any. */
  const [editing, setEditing] = useState<SchoolUser | null>(null);
  const [editScope, setEditScope] = useState<ScopeSelection>(emptySelection);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const load = () =>
    api.school
      .users()
      .then(setData)
      .catch((e: Error) => setError(e.message));

  useEffect(() => {
    void load();
    api.school
      .grantable()
      .then(setTree)
      .catch((e: Error) => setError(e.message));
  }, []);

  const full = !!data && data.seatLimit !== null && data.seatsUsed >= data.seatLimit;

  const addUser = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setNote(null);
    setSaving(true);
    const f = new FormData(e.currentTarget);
    const form = e.currentTarget;
    try {
      const user = await api.school.createUser({
        email: (f.get('email') as string).trim(),
        password: (f.get('password') as string).trim(),
        role: f.get('role') as ManageableRole,
        scope: selectionToEntries(newScope, tree),
      });
      form.reset();
      setNewScope(emptySelection());
      setNote(`${user.email} added.`);
      await load();
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  /** Every row action follows the same shape: run, note, reload, clear busy. */
  const rowAction = async (id: string, message: string, run: () => Promise<unknown>) => {
    setError(null);
    setNote(null);
    setBusy(id);
    try {
      await run();
      setNote(message);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const toggleStatus = (u: SchoolUser) =>
    rowAction(
      u.id,
      u.status === 'ACTIVE' ? `${u.email} suspended.` : `${u.email} reactivated.`,
      () =>
        api.school.updateUser(u.id, {
          status: u.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
        }),
    );

  const resetPassword = (u: SchoolUser) => {
    const password = window.prompt(`New password for ${u.email} (at least 8 characters):`);
    if (!password) return;
    void rowAction(u.id, `Password updated for ${u.email}.`, () =>
      api.school.resetPassword(u.id, password),
    );
  };

  const removeUser = (u: SchoolUser) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    void rowAction(u.id, `${u.email} deleted.`, () => api.school.deleteUser(u.id));
  };

  const openScope = async (u: SchoolUser) => {
    setEditing(u);
    setEditError(null);
    setEditLoading(true);
    setEditScope(emptySelection());
    try {
      setEditScope(selectionFromRows(await api.school.scope(u.id)));
    } catch (err) {
      setEditError((err as Error).message);
    } finally {
      setEditLoading(false);
    }
  };

  const saveScope = async () => {
    if (!editing) return;
    setEditError(null);
    setBusy(editing.id);
    try {
      await api.school.setScope(editing.id, selectionToEntries(editScope, tree));
      setNote(`Access updated for ${editing.email}.`);
      setEditing(null);
    } catch (err) {
      setEditError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-slate-900">
          {data?.schoolName ?? 'School'} — users
        </h1>
        {data && (
          <span
            className={
              'text-sm px-2 py-1 rounded border ' +
              (full
                ? 'bg-amber-50 text-amber-800 border-amber-200'
                : 'bg-slate-50 text-slate-600 border-slate-200')
            }
          >
            {data.seatLimit === null
              ? `${data.seatsUsed} active`
              : `${data.seatsUsed} of ${data.seatLimit} seats used`}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-6">
        Coordinators can add and manage their own teachers. Teachers only watch content.
        Suspended accounts cannot sign in and do not use a seat.
      </p>

      {error && (
        <div className="mb-4">
          <ErrorBox message={error} />
        </div>
      )}
      {note && (
        <p className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
          {note}
        </p>
      )}
      {!data && !error && <Loading />}

      {data && (
        <>
          <div className="overflow-x-auto border border-slate-200 rounded-md bg-white mb-8">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Email</th>
                  <th className="text-left font-medium px-3 py-2">Role</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="text-left font-medium px-3 py-2">Last login</th>
                  <th className="text-right font-medium px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => {
                  const self = u.id === me?.id;
                  // Peer school admins are the super admin's to manage, and the
                  // server rejects self-actions — so neither gets buttons.
                  const managed = !self && (u.role === 'TEACHER' || u.role === 'COORDINATOR');
                  const suspended = u.status === 'SUSPENDED';
                  return (
                    <tr
                      key={u.id}
                      className={'border-t border-slate-100 ' + (suspended ? 'bg-slate-50' : '')}
                    >
                      <td className={'px-3 py-2 ' + (suspended ? 'text-slate-400' : 'text-slate-900')}>
                        {u.email}
                        {self && <span className="text-xs text-slate-400 ml-2">(you)</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{ROLE_LABEL[u.role] ?? u.role}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            'inline-block px-2 py-0.5 rounded border text-xs ' +
                            (suspended
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-green-50 text-green-700 border-green-200')
                          }
                        >
                          {suspended ? 'SUSPENDED' : 'ACTIVE'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {managed ? (
                          <span className="inline-flex gap-2">
                            <button
                              disabled={busy === u.id}
                              onClick={() => void openScope(u)}
                              className={
                                rowBtn +
                                ' border-sky-300 text-sky-800 hover:bg-sky-50 disabled:opacity-50'
                              }
                            >
                              Access
                            </button>
                            <button
                              disabled={busy === u.id}
                              onClick={() => void toggleStatus(u)}
                              className={
                                rowBtn +
                                ' disabled:opacity-50 ' +
                                (suspended
                                  ? 'border-green-300 text-green-800 hover:bg-green-50'
                                  : 'border-amber-300 text-amber-800 hover:bg-amber-50')
                              }
                            >
                              {suspended ? 'Reactivate' : 'Suspend'}
                            </button>
                            <button
                              disabled={busy === u.id}
                              onClick={() => resetPassword(u)}
                              className={
                                rowBtn +
                                ' border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50'
                              }
                            >
                              Reset password
                            </button>
                            <button
                              disabled={busy === u.id}
                              onClick={() => removeUser(u)}
                              className={
                                rowBtn +
                                ' border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50'
                              }
                            >
                              Delete
                            </button>
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {editing && (
            <div className="mb-8 border border-sky-200 bg-sky-50/40 rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-900">
                  Access for {editing.email}
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    {ROLE_LABEL[editing.role] ?? editing.role}
                  </span>
                </h2>
                <button
                  onClick={() => setEditing(null)}
                  className="text-xs text-slate-500 hover:text-slate-900"
                >
                  Close
                </button>
              </div>

              <p className="text-xs text-slate-500 mb-3">
                {editing.role === 'COORDINATOR'
                  ? 'A coordinator can only pass on access they hold themselves, so this also sets the ceiling for the teachers they create.'
                  : 'What this teacher can watch.'}
              </p>

              {editError && (
                <div className="mb-3">
                  <ErrorBox message={editError} />
                </div>
              )}
              {editLoading ? (
                <Loading />
              ) : (
                <>
                  <ScopePicker
                    tree={tree}
                    value={editScope}
                    onChange={setEditScope}
                    disabled={busy === editing.id}
                  />
                  <button
                    onClick={() => void saveScope()}
                    disabled={busy === editing.id}
                    className="mt-4 px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50"
                  >
                    {busy === editing.id ? 'Saving…' : 'Save access'}
                  </button>
                </>
              )}
            </div>
          )}

          <h2 className="text-sm font-semibold text-slate-900 mb-1">Add a user</h2>
          <p className="text-xs text-slate-500 mb-3">
            Give them the password directly — they can sign in straight away.
          </p>

          {formError && (
            <div className="mb-4">
              <ErrorBox message={formError} />
            </div>
          )}

          <form onSubmit={(e) => void addUser(e)} className="space-y-4 max-w-2xl">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className={label} htmlFor="email">
                  Email
                </label>
                <input id="email" name="email" type="email" required className={field} />
              </div>
              <div>
                <label className={label} htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="text"
                  required
                  minLength={8}
                  className={field}
                />
              </div>
              <div>
                <label className={label} htmlFor="role">
                  Role
                </label>
                <select id="role" name="role" defaultValue="TEACHER" className={field}>
                  <option value="TEACHER">Teacher</option>
                  <option value="COORDINATOR">Coordinator</option>
                </select>
              </div>
            </div>

            <div>
              <p className={label}>Access</p>
              <ScopePicker tree={tree} value={newScope} onChange={setNewScope} disabled={saving} />
            </div>

            <button
              type="submit"
              disabled={saving || full}
              className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add user'}
            </button>
            {full && (
              <p className="text-xs text-amber-800">
                All seats are in use. Suspend or delete a user to free one, or ask your
                administrator to raise the limit.
              </p>
            )}
          </form>
        </>
      )}
    </Layout>
  );
}
